import {
  Alert,
  Button,
  Group,
  NumberInput,
  Stack,
  Switch,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { serverErrorText } from "./serverError";
import { useUpdateRepositorySettings } from "./useUpdateRepositorySettings";
import type {
  RepositorySettingsResponse,
  UpdateRepositorySettingsRequest,
} from "./api";

// Редактор настроек репозитория: все настраиваемые поля строки
// `repository_settings`, сгруппированные по секциям. NOT NULL-поля —
// обязательные NumberInput'ы; nullable-override (max_retries, probe_*, executor
// cooldown) — NumberInput, пустое значение = «наследовать дефолт N»;
// migration_path_globs — переключатель наследования + Textarea (пусто
// при выключенном наследовании = явный opt-out []).

// Числовое значение NumberInput: число либо "" (пусто). Для
// обязательных полей "" блокирует сохранение, для override = наследует.
type NumValue = number | string;

// Ключи числовых полей формы (общий каррированный onChange).
type NumKey =
  | "max_retries"
  | "reaper_awaiting_stale_seconds"
  | "reconcile_interval_seconds"
  | "probe_period_seconds"
  | "probe_failure_threshold"
  | "probe_success_threshold"
  | "probe_timeout_seconds"
  | "probe_initial_delay_seconds"
  | "executor_cooldown_seconds";

// Разбить текст глобов на строки: trim + отбросить пустые.
function parseGlobs(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

interface FormState {
  max_retries: NumValue;
  reaper_awaiting_stale_seconds: NumValue;
  reconcile_enabled: boolean;
  reconcile_interval_seconds: NumValue;
  probe_period_seconds: NumValue;
  probe_failure_threshold: NumValue;
  probe_success_threshold: NumValue;
  probe_timeout_seconds: NumValue;
  probe_initial_delay_seconds: NumValue;
  executor_cooldown_seconds: NumValue;
  globsInherit: boolean;
  globsText: string;
}

// "" для null-override (поле наследует дефолт), число иначе.
function overrideValue(value: number | null): NumValue {
  return value ?? "";
}

function initialState(data: RepositorySettingsResponse): FormState {
  return {
    max_retries: overrideValue(data.max_retries),
    reaper_awaiting_stale_seconds: data.reaper_awaiting_stale_seconds,
    reconcile_enabled: data.reconcile_enabled,
    reconcile_interval_seconds: data.reconcile_interval_seconds,
    probe_period_seconds: overrideValue(data.probe_period_seconds),
    probe_failure_threshold: overrideValue(data.probe_failure_threshold),
    probe_success_threshold: overrideValue(data.probe_success_threshold),
    probe_timeout_seconds: overrideValue(data.probe_timeout_seconds),
    probe_initial_delay_seconds: overrideValue(
      data.probe_initial_delay_seconds,
    ),
    executor_cooldown_seconds: overrideValue(data.executor_cooldown_seconds),
    globsInherit: data.migration_path_globs === null,
    globsText:
      data.migration_path_globs === null
        ? ""
        : data.migration_path_globs.join("\n"),
  };
}

// null для пустого override (наследует дефолт), число иначе.
function overrideToBody(value: NumValue): number | null {
  return typeof value === "number" ? value : null;
}

function buildBody(state: FormState): UpdateRepositorySettingsRequest {
  return {
    max_retries: overrideToBody(state.max_retries),
    reaper_awaiting_stale_seconds: Number(state.reaper_awaiting_stale_seconds),
    reconcile_enabled: state.reconcile_enabled,
    reconcile_interval_seconds: Number(state.reconcile_interval_seconds),
    probe_period_seconds: overrideToBody(state.probe_period_seconds),
    probe_failure_threshold: overrideToBody(state.probe_failure_threshold),
    probe_success_threshold: overrideToBody(state.probe_success_threshold),
    probe_timeout_seconds: overrideToBody(state.probe_timeout_seconds),
    probe_initial_delay_seconds: overrideToBody(
      state.probe_initial_delay_seconds,
    ),
    executor_cooldown_seconds: overrideToBody(state.executor_cooldown_seconds),
    migration_path_globs: state.globsInherit
      ? null
      : parseGlobs(state.globsText),
  };
}

// Все обязательные (NOT NULL) поля заданы числом — иначе сохранение
// заблокировано (пустой NumberInput даёт "").
function isSubmittable(state: FormState): boolean {
  const required: NumValue[] = [
    state.reaper_awaiting_stale_seconds,
    state.reconcile_interval_seconds,
  ];
  return required.every((value) => typeof value === "number");
}

export function RepositorySettingsForm({
  repoFullName,
  data,
}: {
  readonly repoFullName: string;
  readonly data: RepositorySettingsResponse;
}): ReactElement {
  const { t } = useTranslation();
  const mutation = useUpdateRepositorySettings();
  const [state, setState] = useState<FormState>(() => initialState(data));

  // Единый каррированный обработчик числовых полей — одно тело на все
  // NumberInput'ы (вместо тринадцати замыканий).
  const onNum =
    (key: NumKey) =>
    (value: NumValue): void => {
      setState((prev) => ({ ...prev, [key]: value }));
    };

  const submittable = isSubmittable(state);
  const serverError =
    mutation.error !== null ? serverErrorText(mutation.error, t) : null;
  const fieldLabel = (key: string): string =>
    t(`repositorySettings.fields.${key}.label`);
  const inheritHint = (defaultValue: number): string =>
    t("repositorySettings.fields.inheritHint", { value: defaultValue });

  return (
    <Stack gap="lg">
      {data.has_row ? null : (
        <Text c="dimmed">{t("repositorySettings.form.usingDefaults")}</Text>
      )}

      <Stack gap="xs">
        <Title order={3}>{t("repositorySettings.groups.retries")}</Title>
        <NumberInput
          label={fieldLabel("max_retries")}
          description={t("repositorySettings.fields.max_retries.inheritHint", {
            value: data.defaults.max_retries,
          })}
          placeholder={String(data.defaults.max_retries)}
          value={state.max_retries}
          min={0}
          max={10}
          allowDecimal={false}
          allowNegative={false}
          clampBehavior="strict"
          onChange={onNum("max_retries")}
        />
        <Text size="sm" c="dimmed">
          {t("repositorySettings.fields.max_retries.effective", {
            value: data.effective_max_retries,
          })}
        </Text>
      </Stack>

      <Stack gap="xs">
        <Title order={3}>{t("repositorySettings.groups.reconcile")}</Title>
        <Switch
          label={fieldLabel("reconcile_enabled")}
          checked={state.reconcile_enabled}
          onChange={(event) => {
            const checked = event.currentTarget.checked;
            setState((prev) => ({ ...prev, reconcile_enabled: checked }));
          }}
        />
        <NumberInput
          label={fieldLabel("reconcile_interval_seconds")}
          value={state.reconcile_interval_seconds}
          min={1}
          allowDecimal={false}
          allowNegative={false}
          clampBehavior="strict"
          onChange={onNum("reconcile_interval_seconds")}
        />
      </Stack>

      <Stack gap="xs">
        <Title order={3}>{t("repositorySettings.groups.reaper")}</Title>
        <NumberInput
          label={fieldLabel("reaper_awaiting_stale_seconds")}
          value={state.reaper_awaiting_stale_seconds}
          min={1}
          allowDecimal={false}
          allowNegative={false}
          clampBehavior="strict"
          onChange={onNum("reaper_awaiting_stale_seconds")}
        />
      </Stack>

      <Stack gap="xs">
        <Title order={3}>{t("repositorySettings.groups.probe")}</Title>
        <NumberInput
          label={fieldLabel("probe_period_seconds")}
          description={inheritHint(data.defaults.probe_period_seconds)}
          placeholder={String(data.defaults.probe_period_seconds)}
          value={state.probe_period_seconds}
          min={1}
          allowDecimal={false}
          allowNegative={false}
          clampBehavior="strict"
          onChange={onNum("probe_period_seconds")}
        />
        <NumberInput
          label={fieldLabel("probe_failure_threshold")}
          description={inheritHint(data.defaults.probe_failure_threshold)}
          placeholder={String(data.defaults.probe_failure_threshold)}
          value={state.probe_failure_threshold}
          min={1}
          allowDecimal={false}
          allowNegative={false}
          clampBehavior="strict"
          onChange={onNum("probe_failure_threshold")}
        />
        <NumberInput
          label={fieldLabel("probe_success_threshold")}
          description={inheritHint(data.defaults.probe_success_threshold)}
          placeholder={String(data.defaults.probe_success_threshold)}
          value={state.probe_success_threshold}
          min={1}
          allowDecimal={false}
          allowNegative={false}
          clampBehavior="strict"
          onChange={onNum("probe_success_threshold")}
        />
        <NumberInput
          label={fieldLabel("probe_timeout_seconds")}
          description={inheritHint(data.defaults.probe_timeout_seconds)}
          placeholder={String(data.defaults.probe_timeout_seconds)}
          value={state.probe_timeout_seconds}
          min={1}
          allowDecimal={false}
          allowNegative={false}
          clampBehavior="strict"
          onChange={onNum("probe_timeout_seconds")}
        />
        <NumberInput
          label={fieldLabel("probe_initial_delay_seconds")}
          description={inheritHint(data.defaults.probe_initial_delay_seconds)}
          placeholder={String(data.defaults.probe_initial_delay_seconds)}
          value={state.probe_initial_delay_seconds}
          min={0}
          allowDecimal={false}
          allowNegative={false}
          clampBehavior="strict"
          onChange={onNum("probe_initial_delay_seconds")}
        />
      </Stack>

      <Stack gap="xs">
        <Title order={3}>{t("repositorySettings.groups.orchestration")}</Title>
        <NumberInput
          label={fieldLabel("executor_cooldown_seconds")}
          description={inheritHint(data.defaults.executor_cooldown_seconds)}
          placeholder={String(data.defaults.executor_cooldown_seconds)}
          value={state.executor_cooldown_seconds}
          min={0}
          allowDecimal={false}
          allowNegative={false}
          clampBehavior="strict"
          onChange={onNum("executor_cooldown_seconds")}
        />
      </Stack>

      <Stack gap="xs">
        <Title order={3}>{t("repositorySettings.groups.migrations")}</Title>
        <Switch
          label={t("repositorySettings.fields.migration_path_globs.inherit")}
          checked={state.globsInherit}
          onChange={(event) => {
            const checked = event.currentTarget.checked;
            setState((prev) => ({ ...prev, globsInherit: checked }));
          }}
        />
        <Textarea
          label={fieldLabel("migration_path_globs")}
          description={t("repositorySettings.fields.migration_path_globs.hint")}
          value={state.globsText}
          disabled={state.globsInherit}
          rows={4}
          onChange={(event) => {
            const value = event.currentTarget.value;
            setState((prev) => ({ ...prev, globsText: value }));
          }}
        />
      </Stack>

      {serverError !== null ? (
        <Alert color="red" title={t("repositorySettings.errorHeading")}>
          {serverError}
        </Alert>
      ) : null}

      <Group justify="flex-end">
        <Button
          onClick={() => {
            mutation.mutate({ repoFullName, body: buildBody(state) });
          }}
          disabled={!submittable}
          loading={mutation.isPending}
        >
          {t("repositorySettings.form.saveButton")}
        </Button>
      </Group>
    </Stack>
  );
}
